import React, { useEffect, useRef, useState } from 'react';

import CourseCard, { isOriginCourse, isSameCourse } from './CourseCard';
import CourseListRow from './CourseListRow';
import { CourseMinifiedDTO } from '../../services/apiClient';

// Divisible by 1, 2, 3 and 4, so a full page never leaves a ragged part-row at any breakpoint.
const PAGE_SIZE = 48;

// Below this the list is dense rows, from it up the card grid. Tailwind's sm (640px), the same
// line the nearby strip and the view toggle's icon switch on, so all three agree on what a phone is.
const PHONE_QUERY = '(max-width: 639.98px)';

const isPhoneWidth = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(PHONE_QUERY).matches;

// Tracked in state, not read once: turning a tablet or dragging a desktop window across the line
// has to swap the layout, or the view toggle's icon (pure CSS) and the list would disagree.
const useIsPhoneWidth = (): boolean => {
  const [isPhone, setIsPhone] = useState(isPhoneWidth);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = (): void => setIsPhone(query.matches);
    query.addEventListener('change', onChange);
    return (): void => query.removeEventListener('change', onChange);
  }, []);

  return isPhone;
};

interface CourseListViewProps {
  // No distance when the list is A to Z and the visitor's location is unknown.
  courses: (CourseMinifiedDTO & { distanceMeters?: number })[];
  // A filter is on, so the list is sorted by name rather than by distance from the origin.
  isAToZ: boolean;
  selectedCourseId?: string;
  onCourseClick: (course: CourseMinifiedDTO) => void;
  onBucketList: (course: CourseMinifiedDTO) => void;
  onRequestToPlay: (course: CourseMinifiedDTO) => void;
  // Whether the signed-in member has each course marked. Passed as predicates rather than as the
  // two Sets so this component never has to know how Explore stores them.
  isPlayed: (course: CourseMinifiedDTO) => boolean;
  isBucketListed: (course: CourseMinifiedDTO) => boolean;
  onTogglePlayed: (course: CourseMinifiedDTO) => void;
  originLabel: string;
  // Same contract as NearbyCoursesStrip: identity of whatever the list is sorted against, so a new
  // origin can reset the scroll and the page size. See that component for why it is not the label.
  originKey: string;
}

// The one non-map view. What a course looks like in it depends on the screen, not on a choice:
// the client cut the separate Card view and asked for dense rows on a phone and the card grid on
// anything wider. Both shapes share the scrolling, the paging and the empty state, so they stay
// one component rather than two that drift.
const CourseListView: React.FC<CourseListViewProps> = ({
  courses,
  isAToZ,
  selectedCourseId,
  onCourseClick,
  onBucketList,
  onRequestToPlay,
  isPlayed,
  isBucketListed,
  onTogglePlayed,
  originLabel,
  originKey,
}) => {
  const isPhone = useIsPhoneWidth();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const hasMore = visibleCount < courses.length;

  // A new origin is a different list, so the page goes back to the top *and* back to one page.
  // Without the count reset, switching origin after scrolling deep would render hundreds of cards
  // for a list the visitor has not looked at yet.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    window.scrollTo({ top: 0 });
  }, [originKey]);

  // Grow as the sentinel comes into view. rootMargin buys a screenful of lead time so the next
  // page is already mounted by the time the visitor reaches the bottom.
  // visibleCount is a dependency because the sentinel unmounts once the list is exhausted and
  // remounts on the next origin — the observer has to re-attach to the new node.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) =>
            Math.min(count + PAGE_SIZE, courses.length),
          );
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return (): void => observer.disconnect();
  }, [courses.length, visibleCount]);

  return (
    <div
      // The page scrolls, not this box: iOS only takes a tap on the status bar back to the top
      // when the document is what scrolls. Explore pins its top bar and drawers with fixed so
      // they stay put over it. min-h keeps a short list covering the screen.
      //
      // Clears the chrome above, which is pinned over this: the 56px top bar, the search box
      // floating under it, and the filter chip row under that, which ends 164px down. 11rem leaves
      // a 12px gap below the chips.
      //
      // One value, no breakpoint. The search box floats at every width now, so there is no size
      // left where it sits inside the bar and this has to shrink to match.
      className="relative z-20 min-h-[100dvh] bg-charcoal pt-[11rem]"
    >
      <div
        className="px-3 pb-10 sm:px-4"
        style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}
      >
        {/* Held back until there is something to be nearest *to* — with no location and no
            course picked, "Nearest to you" sitting above "no courses" reads as a contradiction. */}
        {courses.length > 0 && (
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-primary/80 sm:text-xs">
            {isAToZ ? 'A to Z' : `Nearest to ${originLabel}`}
          </p>
        )}

        {courses.length === 0 ? (
          <p className="text-sm text-white/40">
            {isAToZ
              ? 'No courses match your filters.'
              : 'No courses to show yet. Allow location access, or search for a course to list what is near it.'}
          </p>
        ) : (
          <>
            {isPhone ? (
              // Negative side margins pull the rows out to the full width of the scroller, past
              // the padding the card grid needs. A dense list that stops short of both edges is
              // a card grid with one column, which is the thing this view exists not to be.
              // Phone only, so -mx-3 matches the scroller's phone padding with no sm step.
              <div className="-mx-3 border-t border-white/10">
                {courses.slice(0, visibleCount).map((course) => (
                  <CourseListRow
                    key={course.id}
                    course={course}
                    distanceMeters={course.distanceMeters}
                    isSelected={isSameCourse(course.id, selectedCourseId)}
                    isOrigin={isOriginCourse(course.id, originKey)}
                    isPlayed={isPlayed(course)}
                    onClick={() => onCourseClick(course)}
                    onTogglePlayed={() => onTogglePlayed(course)}
                  />
                ))}
              </div>
            ) : (
              /* gap-y is larger than gap-x: the logo badge hangs below its hero, so rows need
                 more clearance than columns do. */
              /* Four across only from 2xl. At xl a four-column card is too narrow for the two
                 action buttons to sit side by side on one line. */
              /* sm and up only (a phone gets the rows), so it starts at two columns. */
              <div className="grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-3 2xl:grid-cols-4">
                {courses.slice(0, visibleCount).map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    distanceMeters={course.distanceMeters}
                    isSelected={isSameCourse(course.id, selectedCourseId)}
                    isOrigin={isOriginCourse(course.id, originKey)}
                    onClick={() => onCourseClick(course)}
                    isPlayed={isPlayed(course)}
                    isBucketListed={isBucketListed(course)}
                    onTogglePlayed={() => onTogglePlayed(course)}
                    onBucketList={() => onBucketList(course)}
                    onRequestToPlay={() => onRequestToPlay(course)}
                  />
                ))}
              </div>
            )}

            {hasMore && <div ref={sentinelRef} className="h-16" />}
          </>
        )}
      </div>
    </div>
  );
};

export default CourseListView;
